import { CODEBASE1_CONFIG } from '../config/codebase1Config';

/**
 * AWS Vendor WebSocket Service
 * Connects to Codebase 1's AWS WebSocket infrastructure for real-time vendor data
 * 
 * FLOW: Codebase 1 → AWS WebSocket → Codebase 2 (this)
 * LINK: merchantId connects vendors to orders to payments
 * 
 * Receives vendor events: VENDOR_ADDED, VENDOR_STATUS_CHANGED, VENDOR_UPDATED
 */

class AwsVendorWebSocketService {
  constructor() {
    this.ws = null;
    this.vendors = new Map(); // merchantId → vendor data
    this.listeners = new Set();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.isConnected = false;
    this.connectionState = {
      isConnecting: false,
      isReconnecting: false,
      lastConnected: null,
      connectionAttempts: 0
    };
    
    // AWS WebSocket endpoint from Codebase 1
    this.awsWebSocketUrl = CODEBASE1_CONFIG.WEBSOCKET_URL;
    
    // Vendor data API endpoint from Codebase 1
    this.vendorApiUrl = CODEBASE1_CONFIG.VENDOR_API_URL;
    
    // Connection parameters for AWS WebSocket - EXACTLY as Codebase 1 specified
    this.connectionParams = {
      type: 'customer',
      timestamp: Date.now() // Number timestamp, not ISO string
    };
  }

  /**
   * Connect to Codebase 1's AWS WebSocket endpoint
   * This establishes the real-time connection for vendor updates
   */
  connect() {
    if (this.isConnected || this.connectionState.isConnecting) {
      console.log('AWS WebSocket already connected or connecting');
      return;
    }

    this.connectionState.isConnecting = true;
    
    try {
      // Build connection URL with parameters for AWS WebSocket
      // EXACT format: wss://api-id.execute-api.region.amazonaws.com/$default?type=customer&timestamp=timestamp
      const params = new URLSearchParams();
      Object.entries(this.connectionParams).forEach(([key, value]) => {
        params.append(key, value);
      });
      
      const wsUrl = `${this.awsWebSocketUrl}?${params.toString()}`;
      
      console.log('🔌 Connecting to Codebase 1 AWS WebSocket:', wsUrl);
      console.log('🔗 merchantId bridge will link vendors to orders');
      console.log('📡 Connection params:', this.connectionParams);
      
      this.ws = new WebSocket(wsUrl);
      this.setupEventHandlers();
      
    } catch (error) {
      console.error('❌ Error creating AWS WebSocket connection:', error);
      this.connectionState.isConnecting = false;
      this.notifyListeners('error', { error: error.message });
    }
  }

  /**
   * Setup WebSocket event handlers for AWS vendor events
   * Each event contains merchantId for linking vendors to orders
   */
  setupEventHandlers() {
    // Bind methods to ensure 'this' context is correct
    const boundAttemptReconnection = this.attemptReconnection.bind(this);
    const boundNotifyListeners = this.notifyListeners.bind(this);
    const boundHandleVendorEvent = this.handleVendorEvent.bind(this);
    
    this.ws.onopen = () => {
      console.log('✅ Connected to Codebase 1 AWS WebSocket successfully');
      console.log('🔗 Ready to receive real-time vendor updates via merchantId');
      console.log('💡 Note: Welcome message may fail due to Lambda env var issue (this is normal)');
      this.isConnected = true;
      this.connectionState.isConnecting = false;
      this.connectionState.isReconnecting = false;
      this.connectionState.lastConnected = new Date();
      this.reconnectAttempts = 0;
      
      boundNotifyListeners('connected', { status: 'connected' });
      
      // Request initial vendor data from Codebase 1's API
      this.requestInitialVendorData();
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 AWS WebSocket message from Codebase 1:', data);
        
        // Handle vendor events from Codebase 1 via AWS
        // Each event contains merchantId for linking
        boundHandleVendorEvent(data);
        
      } catch (error) {
        // Welcome message might fail due to env var issue - this is expected
        console.log('📨 Raw message received (may be welcome message):', event.data);
        console.log('💡 This is normal if Lambda has env var issues');
      }
    };

    this.ws.onclose = (event) => {
      console.log('🔌 AWS WebSocket connection closed:', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        timestamp: new Date().toISOString()
      });
      this.isConnected = false;
      this.connectionState.isConnecting = false;
      
      boundNotifyListeners('disconnected', { 
        code: event.code, 
        reason: event.reason,
        wasClean: event.wasClean
      });
      
      // Attempt reconnection if not a clean close
      if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
        console.log(`🔄 Attempting reconnection (${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})...`);
        boundAttemptReconnection();
      }
    };

    this.ws.onerror = (error) => {
      console.error('❌ AWS WebSocket error:', {
        error: error.message,
        timestamp: new Date().toISOString(),
        connectionState: this.connectionState
      });
      boundNotifyListeners('error', { error: error.message });
    };
  }

  /**
   * Handle vendor events from Codebase 1 via AWS WebSocket
   * Each event contains merchantId for linking vendors to orders
   * Using exact event format: { type, timestamp, vendor }
   */
  handleVendorEvent(data) {
    try {
      const { type, timestamp, vendor } = data;
      
      // Extract merchantId for linking
      const merchantId = vendor?.merchantId;
      if (!merchantId) {
        console.warn('⚠️ Vendor event missing merchantId:', data);
        return;
      }
      
      console.log(`🔗 Processing ${type} for merchantId: ${merchantId}`);
      
      switch (type) {
        case 'VENDOR_ADDED':
          console.log('🆕 New vendor added from Codebase 1:', vendor.businessName, 'merchantId:', merchantId);
          this.vendors.set(merchantId, {
            ...vendor,
            lastUpdated: timestamp || new Date().toISOString()
          });
          this.notifyListeners('vendorAdded', vendor);
          break;
          
        case 'VENDOR_STATUS_CHANGED':
          console.log('🔄 Vendor status changed from Codebase 1:', vendor.businessName, 'merchantId:', merchantId);
          if (this.vendors.has(merchantId)) {
            const existingVendor = this.vendors.get(merchantId);
            this.vendors.set(merchantId, {
              ...existingVendor,
              ...vendor,
              lastUpdated: timestamp || new Date().toISOString()
            });
            this.notifyListeners('vendorStatusChanged', vendor);
          }
          break;
          
        case 'VENDOR_MENU_UPDATED':
          console.log('📝 Vendor menu updated from Codebase 1:', vendor.businessName, 'merchantId:', merchantId);
          if (this.vendors.has(merchantId)) {
            const existingVendor = this.vendors.get(merchantId);
            this.vendors.set(merchantId, {
              ...existingVendor,
              ...vendor,
              lastUpdated: timestamp || new Date().toISOString()
            });
            this.notifyListeners('vendorMenuUpdated', vendor);
          }
          break;
          
        default:
          console.log('📨 Unknown event type from Codebase 1:', type, data);
          this.notifyListeners('unknownEvent', data);
      }
      
    } catch (error) {
      console.error('❌ Error handling vendor event from Codebase 1:', error);
      this.notifyListeners('error', { error: error.message });
    }
  }

  /**
   * Request initial vendor data from Codebase 1's AWS API
   * This populates the vendor list with current merchantId mappings
   */
  async requestInitialVendorData() {
    try {
      console.log('📡 Requesting initial vendor data from Codebase 1 AWS API...');
      
      const response = await fetch(this.vendorApiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const vendors = await response.json();
      console.log('📡 Received initial vendor data from Codebase 1:', vendors.length, 'vendors');
      
      // Clear existing vendors and populate with fresh data from Codebase 1
      this.vendors.clear();
      vendors.forEach(vendor => {
        const merchantId = vendor.merchantId;
        if (merchantId) {
          console.log(`🔗 Mapping vendor: ${vendor.businessName} → merchantId: ${merchantId}`);
          this.vendors.set(merchantId, {
            ...vendor,
            lastUpdated: new Date().toISOString()
          });
        } else {
          console.warn('⚠️ Vendor missing merchantId:', vendor);
        }
      });
      
      console.log(`✅ Loaded ${this.vendors.size} vendors with merchantId mappings`);
      this.notifyListeners('initialVendorsLoaded', Array.from(this.vendors.values()));
      
    } catch (error) {
      console.error('❌ Error fetching initial vendor data from Codebase 1:', error);
      this.notifyListeners('error', { error: error.message });
    }
  }

  /**
   * Subscribe to vendor events
   */
  subscribe(callback) {
    this.listeners.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Add event listener (alias for subscribe for compatibility)
   */
  addListener(eventType, callback) {
    // Create a wrapper that filters events by type
    const wrappedCallback = (type, data) => {
      if (type === eventType) {
        callback(data);
      }
    };
    
    this.listeners.add(wrappedCallback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(wrappedCallback);
    };
  }

  /**
   * Remove event listener
   */
  removeListener(eventType, callback) {
    // For now, we'll use the subscribe pattern
    // In a more advanced implementation, we'd track event types separately
    this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of an event
   */
  notifyListeners(eventType, data) {
    this.listeners.forEach(callback => {
      try {
        callback(eventType, data);
      } catch (error) {
        console.error('❌ Error in vendor service listener:', error);
      }
    });
  }

  /**
   * Get all vendors (including inactive)
   */
  getVendors() {
    return Array.from(this.vendors.values());
  }

  /**
   * Get only active vendors (for customer display)
   */
  getActiveVendors() {
    return Array.from(this.vendors.values()).filter(vendor => vendor.status === 'active');
  }

  /**
   * Get vendor by merchant ID
   */
  getVendor(merchantId) {
    return this.vendors.get(merchantId);
  }

  /**
   * Get vendors by status
   */
  getVendorsByStatus(status) {
    return Array.from(this.vendors.values()).filter(vendor => vendor.status === status);
  }

  /**
   * Attempt to reconnect
   */
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('❌ Max reconnection attempts reached');
      this.notifyListeners('maxReconnectAttemptsReached');
      return;
    }

    if (this.connectionState.isReconnecting) {
      return;
    }

    this.connectionState.isReconnecting = true;
    this.reconnectAttempts++;
    
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    console.log(`🔄 Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      this.connectionState.isReconnecting = false;
      this.connect();
    }, delay);
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'Client disconnecting');
      this.ws = null;
    }
    
    this.isConnected = false;
    this.connectionState.isConnecting = false;
    this.connectionState.isReconnecting = false;
    
    console.log('🔌 AWS WebSocket disconnected');
    this.notifyListeners('disconnected');
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      connectionState: { ...this.connectionState },
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      vendorCount: this.vendors.size,
      activeVendorCount: this.getActiveVendors().length
    };
  }

  /**
   * Set AWS WebSocket configuration
   */
  setConfiguration(webSocketUrl, apiKey) {
    this.awsWebSocketUrl = webSocketUrl;
    this.apiKey = apiKey;
    console.log('⚙️ AWS WebSocket configuration updated');
  }

  /**
   * Set reconnection parameters
   */
  setReconnectionParams(maxAttempts, delay) {
    this.maxReconnectAttempts = maxAttempts;
    this.reconnectDelay = delay;
    console.log(`⚙️ Reconnection params updated: max=${maxAttempts}, delay=${delay}ms`);
  }
}

// Create singleton instance
const awsVendorWebSocketService = new AwsVendorWebSocketService();

export default awsVendorWebSocketService;
